import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const TZ = 'America/Toronto';

/** Match T2-5749 / API `getFeedbackSurveyPopupJobStartDateMin` (Toronto calendar dates). */
export const FEEDBACK_SURVEY_POPUP_MAX_AGE_DAYS = 30;

/**
 * Coerce any reasonable representation of a Job.startDate into `YYYY-MM-DD`.
 * MySQL DATE columns are serialized either as `"2026-04-21"` (when `dateStrings`
 * is set) or as a full ISO `"2026-04-21T00:00:00.000Z"` (when the mysql driver
 * returns a `Date`). Both need to collapse to the same Toronto calendar date.
 */
function toYmd(input: string | Date | null | undefined): string | null {
  if (!input) return null;
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null;
    return dayjs(input).tz(TZ).format('YYYY-MM-DD');
  }
  const s = String(input).trim();
  if (!s) return null;
  // Pure YYYY-MM-DD (what the DB logically stores).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO string at UTC midnight (MySQL driver default). Keep the calendar day.
  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoMatch) return isoMatch[1];
  const parsed = dayjs(s);
  return parsed.isValid() ? parsed.tz(TZ).format('YYYY-MM-DD') : null;
}

/**
 * True when Job.startDate is within the popup window (on or after "today − 30 days" in Toronto).
 */
export function isJobStartDateEligibleForFeedbackSurveyPopup(
  jobStartDate: string | Date | null | undefined,
): boolean {
  const ymd = toYmd(jobStartDate);
  if (!ymd) return false;
  const todayYmd = dayjs().tz(TZ).format('YYYY-MM-DD');
  const cutoffYmd = dayjs.tz(todayYmd, 'YYYY-MM-DD', TZ)
    .subtract(FEEDBACK_SURVEY_POPUP_MAX_AGE_DAYS, 'day')
    .format('YYYY-MM-DD');
  // Straight calendar-date string compare in ISO format ≡ lexical compare.
  return ymd >= cutoffYmd;
}

/**
 * T2-6185: same inclusive 30-day Toronto window used for View / Edit Feedback.
 * Alias kept so call sites can name the edit intent without implying popup UX.
 */
export function canEditFeedbackForJobStartDate(
  jobStartDate: string | Date | null | undefined,
): boolean {
  return isJobStartDateEligibleForFeedbackSurveyPopup(jobStartDate);
}
